import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { AnvilTheme } from "@repo/blacksmith-ui/anvil/animations";
import { ANVIL_TRANSITION_MANIFEST, type AnvilTransitionVariant } from "@repo/blacksmith-ui/anvil/manifest";
import { useStableCallback } from "@/hooks/useStableCallback";
import { assetUrl } from "@/lib/assetUrl";

/** A phase still loading this long after it is due hard-swaps to the target loop. */
const LOAD_TIMEOUT_MS = 1_000;
// A swap paints up to one 60Hz frame after setState, and each phase's last
// frame repeats as the next phase's first, so seams are issued one
// generator tick (FRAME_MS in scripts/generate-anvil-variants.mjs) early.
const SWAP_PAINT_LEAD_MS = 17;
const FRAME_MS = 30;
interface Exit {
  startMs: number;
  endMs: number;
  outro: number;
  outroMs: number;
}
interface Shown {
  variant: AnvilTransitionVariant;
  theme: AnvilTheme;
  src: string;
  phase: "loop" | "outro" | "intro";
}
const blobs = new Map<string, Promise<Blob>>();

/** One fetch per animation asset, shared by every player that needs it. */
export function fetchBlob(path: string): Promise<Blob> {
  let blob = blobs.get(path);
  if (!blob) {
    blob = fetch(path).then(response => {
      if (!response.ok) throw new Error(`${response.status} ${path}`);
      return response.blob();
    });
    blob.catch(() => blobs.delete(path));
    blobs.set(path, blob);
  }
  return blob;
}
function animationPath(name: string, theme: AnvilTheme): string {
  return assetUrl(`anvil-animations/anvil-${name}-${theme}.png`);
}

/**
 * A fresh object URL for `blob`, so a play-once APNG starts at frame 0,
 * decoded before it is shown: swapping an img to a URL that isn't ready yet
 * can paint the outgoing image's first frame in between.
 */
async function decodedSrc(blob: Blob): Promise<string> {
  const src = URL.createObjectURL(blob);
  const img = new Image();
  img.src = src;
  try {
    await img.decode();
  } catch (error) {
    URL.revokeObjectURL(src);
    throw error;
  }
  return src;
}
interface Decoding {
  src: string;
  failed: boolean;
}

/** Like `decodedSrc`, but returns at once so a swap never waits on the decode. */
function startDecode(blob: Blob): Decoding {
  const decoding = {
    src: URL.createObjectURL(blob),
    failed: false
  };
  const img = new Image();
  img.src = decoding.src;
  img.decode().catch(() => {
    decoding.failed = true;
  });
  return decoding;
}
function revokeAll(srcs: string[]): void {
  for (const src of srcs) URL.revokeObjectURL(src);
}

/** Rejects if any blob fails to decode, revoking the ones that did. */
async function decodedSrcs(blobs: Blob[]): Promise<string[]> {
  const results = await Promise.allSettled(blobs.map(decodedSrc));
  const srcs = results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
  if (srcs.length < results.length) {
    revokeAll(srcs);
    throw new Error("anvil animation failed to decode");
  }
  return srcs;
}
function revokeLater(src: string): void {
  if (!src.startsWith("blob:")) return;
  // After the img has swapped off it.
  setTimeout(() => URL.revokeObjectURL(src), 1_000);
}

/** Every outro of `variant` by `Exit.outro`, named as the generator writes them. */
function outroNames(variant: AnvilTransitionVariant): string[] {
  const exits: readonly Exit[] = ANVIL_TRANSITION_MANIFEST[variant].exits;
  const count = Math.max(...exits.map(exit => exit.outro)) + 1;
  return Array.from({
    length: count
  }, (_, outro) => outro === 0 ? `${variant}-outro` : `${variant}-outro-${outro}`);
}

/**
 * The next exit the loop reaches with time left to paint the swap, e.g.
 * running (exits at 0, 480, 960 of 1440) 100ms in → the 480 one, 380ms away.
 */
function nextExit(exits: readonly Exit[], loopMs: number, intoLoop: number): {
  exit: Exit;
  inMs: number;
} {
  for (const exit of exits) {
    if (intoLoop + SWAP_PAINT_LEAD_MS <= exit.endMs) {
      return {
        exit,
        inMs: Math.max(0, exit.startMs - intoLoop)
      };
    }
  }
  return {
    exit: exits[0],
    inMs: loopMs - intoLoop
  };
}
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, Math.max(0, ms)));
function withLoadTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([promise, wait(LOAD_TIMEOUT_MS).then(() => {
    throw new Error("anvil transition load timed out");
  })]);
}
const nextPaint = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

/**
 * The src for an `active` anvil that plays `loop → outro → intro → loop`
 * when `target` changes, e.g. sleuthing → sleuthing-outro → running-intro →
 * running. `src` is null while the anvil is not `active`; entering `active`,
 * mounting, or a theme switch shows the target directly. `holding` is true
 * from a target change until its outro starts, so a label that changed with
 * the target can roll in step with the character.
 */
export function useAnvilActiveTransition(target, theme) {
  const $ = _c(25);
  const [shown, setShown] = useState(null);
  const shownRef = useRef(null);
  let t0;
  if ($[0] !== target || $[1] !== theme) {
    t0 = {
      target,
      theme
    };
    $[0] = target;
    $[1] = theme;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const latestRef = useRef(t0);
  const loopStartRef = useRef(0);
  const runningRef = useRef(false);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  let t1;
  if ($[3] !== target || $[4] !== theme) {
    t1 = () => {
      latestRef.current = {
        target,
        theme
      };
    };
    $[3] = target;
    $[4] = theme;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useLayoutEffect(t1);
  const resting = shown?.theme === theme ? shown : null;
  let t2;
  if ($[6] !== resting || $[7] !== target || $[8] !== theme) {
    t2 = target === null ? null : resting ?? {
      variant: target,
      theme,
      src: animationPath(target, theme),
      phase: "loop" as const
    };
    $[6] = resting;
    $[7] = target;
    $[8] = theme;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const current = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = next => {
      const previous = shownRef.current;
      shownRef.current = next;
      setShown(next);
      if (previous && previous.src !== next?.src) {
        revokeLater(previous.src);
      }
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const show = useStableCallback(t3);
  let t4;
  let t5;
  if ($[11] !== show || $[12] !== target || $[13] !== theme) {
    t4 = () => {
      const hardSwap = next_0 => {
        generationRef.current = generationRef.current + 1;
        runningRef.current = false;
        show(next_0);
      };
      if (target === null) {
        if (shownRef.current) {
          hardSwap(null);
        }
        return;
      }
      if (shownRef.current?.theme !== theme) {
        hardSwap({
          variant: target,
          theme,
          src: animationPath(target, theme),
          phase: "loop"
        });
        loopStartRef.current = performance.now();
        return;
      }
      if (shownRef.current.variant === target || runningRef.current) {
        return;
      }
      const generation = generationRef.current;
      const stale = () => !mountedRef.current || generationRef.current !== generation || latestRef.current.target === null || latestRef.current.theme !== theme;
      const transition = async (from, to) => {
        const fetchAll = names => Promise.all(names.map(name => fetchBlob(animationPath(name, theme))));
        const nextBlobs = fetchAll([`${to}-intro`, to]);
        nextBlobs.catch(_temp);
        let next_1;
        let nextTaken = false;
        const play = async () => {
          const outros = await withLoadTimeout(fetchAll(outroNames(from)));
          if (stale() || latestRef.current.target === from) {
            return false;
          }
          const {
            loopMs,
            exits
          } = ANVIL_TRANSITION_MANIFEST[from];
          const intoLoop = (performance.now() - loopStartRef.current) % loopMs;
          const {
            exit,
            inMs
          } = nextExit(exits, loopMs, intoLoop);
          const outro = startDecode(outros[exit.outro]);
          await wait(inMs - SWAP_PAINT_LEAD_MS);
          if (stale()) {
            URL.revokeObjectURL(outro.src);
            return false;
          }
          if (outro.failed) {
            URL.revokeObjectURL(outro.src);
            throw new Error("anvil outro failed to decode");
          }
          show({
            variant: from,
            theme,
            src: outro.src,
            phase: "outro"
          });
          await nextPaint();
          next_1 = nextBlobs.then(decodedSrcs);
          next_1.catch(_temp2);
          await wait(exit.outroMs - FRAME_MS);
          const [intro, loop] = await withLoadTimeout(next_1);
          nextTaken = true;
          if (stale()) {
            revokeAll([intro, loop]);
            return false;
          }
          show({
            variant: from,
            theme,
            src: intro,
            phase: "intro"
          });
          await nextPaint();
          await wait(ANVIL_TRANSITION_MANIFEST[to].introMs - FRAME_MS);
          if (stale()) {
            URL.revokeObjectURL(loop);
            return false;
          }
          show({
            variant: to,
            theme,
            src: loop,
            phase: "loop"
          });
          await nextPaint();
          return true;
        };
        const played = await play().catch(() => {
          if (stale()) {
            return false;
          }
          show({
            variant: to,
            theme,
            src: animationPath(to, theme),
            phase: "loop"
          });
          return true;
        }).finally(() => {
          if (!nextTaken) {
            next_1?.then(revokeAll, _temp3);
          }
        });
        if (!played) {
          return false;
        }
        loopStartRef.current = performance.now();
        return true;
      };
      runningRef.current = true;
      const run = async () => {
        while (true) {
          const from_0 = shownRef.current;
          const to_0 = latestRef.current.target;
          if (!from_0 || to_0 === null || from_0.variant === to_0) {
            return;
          }
          if (!(await transition(from_0.variant, to_0))) {
            return;
          }
        }
      };
      run().finally(() => {
        if (generationRef.current === generation) {
          runningRef.current = false;
        }
      });
    };
    t5 = [target, theme, show];
    $[11] = show;
    $[12] = target;
    $[13] = theme;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  const loopVariant = current?.phase === "loop" ? current.variant : null;
  let t6;
  let t7;
  if ($[16] !== loopVariant || $[17] !== theme) {
    t6 = () => {
      if (!loopVariant) {
        return;
      }
      for (const name_0 of outroNames(loopVariant)) {
        fetchBlob(animationPath(name_0, theme));
      }
    };
    t7 = [loopVariant, theme];
    $[16] = loopVariant;
    $[17] = theme;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (shownRef.current) {
          revokeLater(shownRef.current.src);
        }
      };
    };
    t9 = [];
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  const t10 = current?.src ?? null;
  const t11 = current !== null && current.phase === "loop" && current.variant !== target;
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = {
      src: t10,
      holding: t11
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function _temp3() {}
function _temp2() {}
function _temp() {}